import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ThreeLineStack } from '../shared/ThreeLineStack';
import { useDashboardLocale } from '../shared/dashboardLocale';
import type { DashboardMessageKey } from '../shared/dashboardMessages';
import { u } from '../shared/hardcodedKoEn';
import { bithumbH3, bithumbMuted } from '../kiwoom/kiwoomTheme';
import {
  fetchBithumbMidnightPaper,
  type BithumbAllowlistSeed,
  type BithumbLiveStack,
  type BithumbLiveStackDay,
  type BithumbPaperSnapshot,
  type BithumbPaperTrade,
  type BithumbWatchRow,
} from './bithumbMidnightPaper';

/** Binance catalystSeedAlarm — 성적 button blinks when allowlist daily is bad. */
function allowlistSeedAlarm(
  seed: BithumbAllowlistSeed | null | undefined,
  t: DashboardT,
): string | null {
  const st = seed?.status;
  if (!st || st === 'ok' || st === 'disabled') return null;
  const detail = seed?.detail ? ` — ${seed.detail}` : '';
  if (st === 'failed') return t('bithumb.spot.allowlist.failed', { detail });
  if (st === 'stale') return t('bithumb.spot.allowlist.stale', { detail });
  return t('bithumb.spot.allowlist.missing', { detail });
}

type DashboardT = (
  key: DashboardMessageKey,
  params?: Record<string, string | number>,
) => string;

const POLL_MS = 5_000;
const POS = '#3fb950';
const NEG = '#f85149';
const ACCENT = '#ff6c00';
const TEXT = '#f5f0e6';

const th: React.CSSProperties = {
  textAlign: 'left',
  color: ACCENT,
  fontSize: '0.7rem',
  padding: '6px 8px',
  borderBottom: `1px solid ${ACCENT}44`,
  whiteSpace: 'nowrap',
};
const td: React.CSSProperties = {
  color: TEXT,
  fontSize: '0.75rem',
  padding: '6px 8px',
  borderBottom: `1px solid ${ACCENT}22`,
  verticalAlign: 'top',
};

type SpotRow = {
  key: string;
  base: string;
  market: string;
  alertHm: string;
  tier: string | null;
  segmentPct: number | null;
  boardPct: number | null;
  mom5: number | null;
  q1h: number | null;
  status: 'watch' | 'open' | 'closed';
  flowLabel: string;
  flowColor: string;
  detail: [string, string, string];
  net: number | null;
  open: boolean;
};

function pct(n: number | null | undefined, d = 1): string {
  if (n == null || Number.isNaN(n)) return '–';
  return `${n >= 0 ? '+' : ''}${n.toFixed(d)}%`;
}

function hmFromKst(kst?: string): string {
  if (!kst) return '–';
  const m = kst.match(/(\d{2}:\d{2})/);
  return m ? m[1] : '–';
}

function hmFromIso(iso?: string): string {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      timeZone: 'Asia/Seoul',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return '–';
  }
}

function shortTime(iso?: string): string {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      timeZone: 'Asia/Seoul',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso.slice(0, 16);
  }
}

function Stat({ label, value, color, sub }: {
  label: string;
  value: React.ReactNode;
  color?: string;
  sub?: React.ReactNode;
}) {
  return (
    <div className="binance-stat-item">
      <div className="binance-stat-label">{label}</div>
      <div className="binance-stat-value" style={{ color: color ?? TEXT }}>{value}</div>
      {sub != null && (
        <div style={{ marginTop: 2, fontSize: '0.66rem', color: bithumbMuted, lineHeight: 1.35 }}>{sub}</div>
      )}
    </div>
  );
}

function StatusLine({ time, label, color }: { time?: string; label: string; color: string }) {
  return (
    <div className="binance-status-line">
      <span className="binance-status-time">{time ?? '–'}</span>
      <span style={{ color, fontWeight: 600 }}>{label}</span>
    </div>
  );
}

function flowFromWatch(
  w: BithumbWatchRow,
  t: DashboardT,
): { label: string; color: string } {
  switch (w.status) {
    case 'ready':
      return { label: t('bithumb.spot.status.ready'), color: POS };
    case 'open':
      return { label: t('binance.status.open'), color: ACCENT };
    case 'expired':
      return { label: t('bithumb.spot.status.expired'), color: NEG };
    case 'wait_window':
      return { label: t('bithumb.spot.status.wait'), color: bithumbMuted };
    case 'not_allowlisted':
      return { label: t('binance.status.skip'), color: bithumbMuted };
    case 'has_mark':
      return { label: t('binance.status.skip'), color: bithumbMuted };
    case 'q1h_blocked':
      return { label: t('binance.status.skip'), color: NEG };
    case 'overshoot':
      return { label: t('binance.status.skip'), color: NEG };
    case 'no_ticks':
      return { label: t('binance.status.skip'), color: NEG };
    default:
      return { label: t('binance.status.watch'), color: bithumbMuted };
  }
}

function buildRows(snap: BithumbPaperSnapshot, t: DashboardT): SpotRow[] {
  const openBy = new Map((snap.open ?? []).map((p) => [p.base, p]));
  const closedBy = new Map<string, BithumbPaperTrade>();
  for (const t of snap.closed ?? []) {
    if (!closedBy.has(t.base)) closedBy.set(t.base, t);
  }
  const seen = new Set<string>();
  const out: SpotRow[] = [];

  const pushWatch = (w: BithumbWatchRow) => {
    seen.add(w.base);
    const op = openBy.get(w.base);
    const cl = closedBy.get(w.base);
    const market = w.market || `KRW-${w.base}`;
    const alertHm = hmFromKst(w.alert_at_kst);
    const tier = w.tier ?? null;
    const segLine = (seg: number | null | undefined) => (
      seg != null ? t('bithumb.spot.detail.segRise', { pct: seg.toFixed(2) }) : '–'
    );
    if (op) {
      out.push({
        key: `open-${w.base}-${op.entered_at}`,
        base: w.base,
        market,
        alertHm,
        tier: op.tier ?? tier,
        segmentPct: w.segment_pct ?? null,
        boardPct: w.board_pct ?? null,
        mom5: w.mom5 ?? null,
        q1h: w.q1h ?? op.q1h ?? null,
        status: 'open',
        flowLabel: t('binance.status.open'),
        flowColor: ACCENT,
        detail: [
          tier ? `tier ${tier}` : segLine(w.segment_pct),
          op.mark != null ? `mark ${op.mark}` : '–',
          op.age_m != null ? `${op.age_m}m` : '–',
        ],
        net: op.unreal_net ?? null,
        open: true,
      });
      return;
    }
    if (cl) {
      out.push({
        key: `closed-${w.base}-${cl.exited_at}`,
        base: w.base,
        market,
        alertHm,
        tier: cl.tier ?? tier,
        segmentPct: w.segment_pct ?? null,
        boardPct: w.board_pct ?? null,
        mom5: w.mom5 ?? null,
        q1h: w.q1h ?? cl.q1h ?? null,
        status: 'closed',
        flowLabel: (cl.reason || t('binance.status.exit')).toUpperCase(),
        flowColor: bithumbMuted,
        detail: [
          cl.tier ? `tier ${cl.tier}` : segLine(w.segment_pct),
          cl.reason ?? '–',
          cl.hold_m != null ? `hold ${cl.hold_m}m` : '–',
        ],
        net: cl.net ?? null,
        open: false,
      });
      return;
    }
    const flow = flowFromWatch(w, t);
    const detailReason = w.status === 'expired'
      ? t('bithumb.spot.detail.sessionEnd')
      : w.status === 'wait_window'
        ? t('bithumb.spot.detail.waitWindow')
        : w.status === 'not_allowlisted'
          ? t('bithumb.spot.detail.notAllowlisted')
          : w.status === 'has_mark'
            ? t('bithumb.spot.detail.hasMark')
            : w.status === 'q1h_blocked'
              ? t('bithumb.spot.detail.q1hBlocked')
              : w.status === 'overshoot'
                ? t('bithumb.spot.detail.overshoot')
                : segLine(w.segment_pct);
    out.push({
      key: `watch-${w.base}-${w.alert_at_kst || ''}`,
      base: w.base,
      market,
      alertHm,
      tier,
      segmentPct: w.segment_pct ?? null,
      boardPct: w.board_pct ?? null,
      mom5: w.mom5 ?? null,
      q1h: w.q1h ?? null,
      status: 'watch',
      flowLabel: flow.label,
      flowColor: flow.color,
      detail: [
        detailReason,
        w.board_pct != null ? `board ${pct(w.board_pct, 2)}` : '–',
        w.q1h != null ? `q1h ${w.q1h.toFixed(1)}` : (w.mom5 != null ? `mom5 ${pct(w.mom5, 2)}` : '–'),
      ],
      net: null,
      open: false,
    });
  };

  const watch = [...(snap.watch ?? [])].sort((a, b) =>
    (b.alert_at_kst || '').localeCompare(a.alert_at_kst || ''),
  );
  for (const w of watch) pushWatch(w);

  for (const op of snap.open ?? []) {
    if (seen.has(op.base)) continue;
    out.push({
      key: `open-${op.base}-${op.entered_at}`,
      base: op.base,
      market: op.market || `KRW-${op.base}`,
      alertHm: hmFromIso(op.entered_at),
      tier: op.tier ?? null,
      segmentPct: null,
      boardPct: op.board_pct ?? null,
      mom5: op.mom5 ?? null,
      q1h: op.q1h ?? null,
      status: 'open',
      flowLabel: t('binance.status.open'),
      flowColor: ACCENT,
      detail: [
        op.tier ? `tier ${op.tier}` : '–',
        op.mark != null ? `mark ${op.mark}` : '–',
        op.age_m != null ? `${op.age_m}m` : '–',
      ],
      net: op.unreal_net ?? null,
      open: true,
    });
  }
  for (const cl of [...(snap.closed ?? [])].reverse()) {
    if (seen.has(cl.base)) continue;
    seen.add(cl.base);
    out.push({
      key: `closed-${cl.base}-${cl.exited_at}`,
      base: cl.base,
      market: cl.market || `KRW-${cl.base}`,
      alertHm: hmFromIso(cl.exited_at),
      tier: cl.tier ?? null,
      segmentPct: null,
      boardPct: cl.board_pct ?? null,
      mom5: cl.mom5 ?? null,
      q1h: cl.q1h ?? null,
      status: 'closed',
      flowLabel: (cl.reason || t('binance.status.exit')).toUpperCase(),
      flowColor: bithumbMuted,
      detail: [
        cl.tier ? `tier ${cl.tier}` : (cl.reason ?? '–'),
        cl.hold_m != null ? `hold ${cl.hold_m}m` : '–',
        '–',
      ],
      net: cl.net ?? null,
      open: false,
    });
  }
  return out;
}

/** Binance scoreDayAxis 와 동일: 2026-08-07 ~ 오늘 MM-DD. */
function scoreDayAxis(): string[] {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const cur = new Date(2026, 7, 7);
  const out: string[] = [];
  while (cur <= end) {
    const mm = String(cur.getMonth() + 1).padStart(2, '0');
    const dd = String(cur.getDate()).padStart(2, '0');
    out.push(`${mm}-${dd}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

const STACK_LABELS = [
  'n', u('건당'), u('승률'), u('최악'), u('합계'), 'MDD', u('실현%eq'), u('잔고밀림'),
] as const;

function dayKey(d: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d.slice(5);
  return d;
}

type StackMetric = {
  n?: number;
  avg_net_pct?: number | null;
  mean_net?: number | null;
  win_rate_pct?: number | null;
  wr?: number | null;
  worst_pct?: number | null;
  sum_pct?: number | null;
  sum_net?: number | null;
  mdd?: number | null;
  realized_eq_pct?: number | null;
  worst_eq_pct?: number | null;
  has_data?: boolean;
};

function metricCell(
  key: string,
  d: StackMetric | undefined,
  winCut: number,
): { text: string; color: string } {
  const miss = !d || d.has_data === false || !(d.n && d.n > 0);
  if (key === 'n') {
    return miss ? { text: '–', color: bithumbMuted } : { text: String(d!.n), color: bithumbMuted };
  }
  const avg = d?.avg_net_pct ?? d?.mean_net;
  const wr = d?.win_rate_pct ?? d?.wr;
  const sum = d?.sum_pct ?? d?.sum_net;
  if (key === '건당') {
    if (miss || avg == null) return { text: '–', color: bithumbMuted };
    return { text: pct(avg, 2), color: avg >= 0 ? POS : NEG };
  }
  if (key === '승률') {
    if (miss || wr == null) return { text: '–', color: bithumbMuted };
    return { text: `${wr.toFixed(0)}%`, color: wr >= winCut ? POS : NEG };
  }
  if (key === '최악') {
    if (miss || d?.worst_pct == null) return { text: '–', color: bithumbMuted };
    return { text: pct(d.worst_pct, 2), color: d.worst_pct >= 0 ? POS : NEG };
  }
  if (key === '합계') {
    if (miss || sum == null) return { text: '–', color: bithumbMuted };
    return { text: pct(sum, 1), color: sum >= 0 ? POS : NEG };
  }
  if (key === 'MDD') {
    if (miss || d?.mdd == null) return { text: '–', color: bithumbMuted };
    return { text: pct(d.mdd, 1), color: d.mdd >= 0 ? POS : NEG };
  }
  // Binance scoreSignedCell(…, '%eq') — 없는 날은 흐리게
  if (key === '실현%eq') {
    if (d?.realized_eq_pct == null) return { text: '–', color: '#3a352c' };
    const v = d.realized_eq_pct;
    return { text: `${v >= 0 ? '+' : ''}${v.toFixed(1)}%eq`, color: v >= 0 ? POS : NEG };
  }
  if (key === '잔고밀림') {
    if (d?.worst_eq_pct == null) return { text: '–', color: '#3a352c' };
    const v = d.worst_eq_pct;
    return { text: `${v >= 0 ? '+' : ''}${v.toFixed(1)}%eq`, color: v >= 0 ? POS : NEG };
  }
  return { text: '–', color: bithumbMuted };
}

/**
 * Binance ScoreDateFrame 과 동일: 지표 | 날짜클립 | 계.
 * 가로 스크롤은 가운데만 움직이고 좌·우 칸을 덮지 않는다.
 */
function LiveStackTable({ stack }: { stack: BithumbLiveStack; t?: DashboardT }) {
  const axis = useMemo(() => scoreDayAxis(), []);
  const byDay = useMemo(() => {
    const m = new Map<string, BithumbLiveStackDay & StackMetric>();
    for (const d of stack.days ?? []) m.set(dayKey(d.day), d as BithumbLiveStackDay & StackMetric);
    return m;
  }, [stack.days]);
  const span = (stack.span ?? undefined) as StackMetric | undefined;
  const barRef = React.useRef<HTMLDivElement>(null);
  const clipRefs = React.useRef<HTMLDivElement[]>([]);
  const leftRef = React.useRef(0);
  const userMoved = React.useRef(false);
  const lock = React.useRef(false);

  const applyX = useCallback((x: number) => {
    leftRef.current = x;
    clipRefs.current.forEach((clip) => {
      const inner = clip?.firstElementChild as HTMLElement | null;
      if (inner) inner.style.transform = `translateX(${-x}px)`;
    });
    const bar = barRef.current;
    if (bar) {
      lock.current = true;
      if (Math.abs(bar.scrollLeft - x) > 1) bar.scrollLeft = x;
      lock.current = false;
    }
  }, []);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return undefined;
    const pin = () => {
      const max = Math.max(0, bar.scrollWidth - bar.clientWidth);
      applyX(userMoved.current ? Math.min(leftRef.current, max) : max);
    };
    pin();
    const onScroll = () => {
      if (lock.current) return;
      if (Math.abs(bar.scrollLeft - leftRef.current) <= 1) return;
      userMoved.current = true;
      applyX(bar.scrollLeft);
    };
    bar.addEventListener('scroll', onScroll, { passive: true });
    const id = requestAnimationFrame(pin);
    return () => {
      bar.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(id);
    };
  }, [applyX, axis.length, stack.days?.length]);

  useEffect(() => {
    const clips = clipRefs.current.filter(Boolean);
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      const bar = barRef.current;
      if (!bar) return;
      const max = Math.max(0, bar.scrollWidth - bar.clientWidth);
      userMoved.current = true;
      applyX(Math.max(0, Math.min(max, leftRef.current + e.deltaX)));
    };
    clips.forEach((el) => el.addEventListener('wheel', onWheel, { passive: false }));
    return () => clips.forEach((el) => el.removeEventListener('wheel', onWheel));
  }, [applyX, axis.length]);

  const setClip = useCallback((i: number, el: HTMLDivElement | null) => {
    if (el) clipRefs.current[i] = el;
  }, []);

  const dayW = '4.5rem';
  const labW = '7.2rem';
  const base: React.CSSProperties = {
    padding: '4px 6px',
    fontSize: '0.64rem',
    fontVariantNumeric: 'tabular-nums',
    boxSizing: 'border-box',
    background: '#0f0f0f',
    height: '100%',
  };
  const labStyle = (head: boolean): React.CSSProperties => ({
    ...base,
    textAlign: 'left',
    color: head ? ACCENT : '#8b949e',
    fontWeight: head ? 600 : 400,
    borderRight: `2px solid ${ACCENT}44`,
  });
  const dayStyle = (head: boolean): React.CSSProperties => ({
    ...base,
    textAlign: 'right',
    color: head ? ACCENT : undefined,
    fontWeight: head ? 600 : 400,
    borderLeft: `1px solid ${ACCENT}22`,
    width: dayW,
    minWidth: dayW,
    maxWidth: dayW,
    verticalAlign: 'top',
  });
  const totStyle = (head: boolean): React.CSSProperties => ({
    ...dayStyle(head),
    fontWeight: head ? 600 : 700,
    color: head ? ACCENT : undefined,
    borderLeft: `2px solid ${ACCENT}55`,
  });
  const rowGrid: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `${labW} minmax(0, 1fr) ${dayW}`,
    alignItems: 'stretch',
  };
  const daysInner = (nodes: React.ReactNode) => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${axis.length}, ${dayW})`,
      width: `calc(${axis.length} * ${dayW})`,
      height: '100%',
    }}
    >
      {nodes}
    </div>
  );

  const body = [
    { key: '__h', head: true as const, label: u('지표'), tot: u('계') as React.ReactNode },
    ...STACK_LABELS.map((label) => {
      const c = metricCell(
        label,
        span && (span.n ?? 0) > 0 ? { ...span, has_data: true } : undefined,
        80,
      );
      return {
        key: label,
        head: false as const,
        label,
        tot: <span style={{ color: c.color }}>{c.text}</span>,
      };
    }),
  ];

  return (
    <div>
      <div style={{ color: ACCENT, fontSize: '0.72rem', fontWeight: 600, margin: '6px 0 4px' }}>
        Spot
      </div>
      <style>{`
        .bithumb-score-date-clip {
          overflow: hidden;
          min-width: 0;
          touch-action: pan-y;
        }
        .bithumb-score-date-bar {
          overflow-x: scroll;
          overflow-y: hidden;
          height: 14px;
          min-width: 0;
          scrollbar-width: auto;
          scrollbar-color: #b7a882 #1c1914;
        }
        .bithumb-score-date-bar::-webkit-scrollbar { display: block; height: 14px; }
        .bithumb-score-date-bar::-webkit-scrollbar-thumb {
          background: #b7a882;
          border-radius: 4px;
        }
        .bithumb-score-date-bar::-webkit-scrollbar-track { background: #1c1914; }
      `}</style>
      <div style={{ marginTop: 4 }}>
        {body.map((row, i) => (
          <div
            key={row.key}
            style={{
              ...rowGrid,
              borderTop: row.head ? 'none' : `1px solid ${ACCENT}18`,
            }}
          >
            <div style={labStyle(row.head)}>{row.label}</div>
            <div
              className="bithumb-score-date-clip"
              ref={(el) => setClip(i, el)}
            >
              {daysInner(row.head
                ? axis.map((day) => (
                  <div key={day} style={dayStyle(true)}>{day}</div>
                ))
                : axis.map((day) => {
                  const c = metricCell(row.label, byDay.get(day), 80);
                  return (
                    <div key={day} style={{ ...dayStyle(false), color: c.color }}>
                      {c.text}
                    </div>
                  );
                }))}
            </div>
            <div style={totStyle(row.head)}>{row.tot}</div>
          </div>
        ))}
        <div style={rowGrid}>
          <div />
          <div ref={barRef} className="bithumb-score-date-bar">
            <div style={{ width: `calc(${axis.length} * ${dayW})`, height: 1 }} />
          </div>
          <div />
        </div>
      </div>
      {stack.note ? (
        <div style={{ marginTop: 6, color: '#7d7362', fontSize: '0.62rem', lineHeight: 1.45 }}>
          {stack.note}
        </div>
      ) : null}
    </div>
  );
}

/** Binance ScoreStackDetail — 규칙 표 (건별 원장 아님). */
function StackRulesDetail({
  snap,
  onClose,
  t,
}: {
  snap: BithumbPaperSnapshot;
  onClose: () => void;
  t: DashboardT;
}) {
  const cfg = snap.config ?? {};
  // Rule cell values stay technical (same on FR/EN); group headers localize.
  const groups: { group: string; cols: { label: string; value: string; hint: string }[] }[] = [
    {
      group: t('bithumb.spot.rule.entry'),
      cols: [
        { label: 'board', value: '≥5%', hint: 'tick vs 60m low' },
        { label: 'mom', value: 'mom5≥1%', hint: 'vs tick 5m ago' },
        { label: 'overshoot', value: 'skip', hint: 'skip if board+10% high' },
        { label: 'window', value: 'KST 22–01', hint: 'ENTRY hours' },
        { label: 'universe', value: 'night cluster', hint: 'segment_rise gap≤15m n≥3' },
      ],
    },
    {
      group: 'filter',
      cols: [
        {
          label: 'Core∪sat',
          value: 'OFF',
          hint: 'Oct oracle — no allowlist tiers',
        },
        {
          label: 'mark_out',
          value: cfg.mark_out_only === false ? 'OFF' : 'ON',
          hint: 'skip bases with USDT-M mark klines',
        },
        {
          label: 'window',
          value: 'KST 22–01',
          hint: 'same as research h2201',
        },
      ],
    },
    {
      group: t('bithumb.spot.rule.exit'),
      cols: [
        { label: 'TP', value: 'board+10%', hint: 'from entry board' },
        { label: 'SL', value: '5%', hint: 'from entry' },
        { label: 'hold', value: '180m', hint: 'timeout market' },
        { label: 'cost', value: '10bp', hint: 'round-trip' },
      ],
    },
    {
      group: t('bithumb.spot.rule.order'),
      cols: [
        { label: 'mode', value: 'paper', hint: 'no live orders' },
        { label: 'ticks', value: 'trade tick', hint: 'no 1m bars · bithumb_ws_batch' },
        { label: 'stack', value: 'live=order', hint: 'score table = current rules' },
        { label: 'sizing', value: '100%', hint: 'realized%eq · FIT_BASE 100' },
      ],
    },
  ];
  const td: React.CSSProperties = {
    padding: '6px 8px',
    borderTop: `1px solid ${ACCENT}22`,
    verticalAlign: 'top',
    fontSize: '0.66rem',
    lineHeight: 1.45,
    color: '#e8dcc0',
    whiteSpace: 'nowrap',
  };
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('bithumb.spot.liveStack.detail')}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10001,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: 16, overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#12100c',
          border: `1px solid ${ACCENT}55`,
          borderRadius: 8, padding: '14px 16px', maxWidth: 960, width: '100%',
          margin: '24px 0', color: '#e8dcc0', fontSize: '0.78rem', lineHeight: 1.5,
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4,
        }}
        >
          <strong style={{ color: ACCENT, fontSize: '0.95rem' }}>
            {t('bithumb.spot.liveStack.detail')}
          </strong>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none', border: `1px solid ${ACCENT}55`, color: '#e8dcc0',
              borderRadius: 4, cursor: 'pointer', padding: '2px 8px',
            }}
          >
            {t('common.close')}
          </button>
        </div>
        <div style={{ marginBottom: 8, color: '#7d7362', fontSize: '0.62rem', lineHeight: 1.45 }}>
          {t('bithumb.spot.liveStack.detailNote')}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.66rem' }}>
            <thead>
              <tr style={{ color: ACCENT }}>
                <th style={{ ...td, borderTop: 'none', textAlign: 'left' }}>
                  {t('bithumb.spot.lane')}
                </th>
                {groups.map((g) => (
                  <th
                    key={g.group}
                    colSpan={g.cols.length}
                    style={{
                      ...td, borderTop: 'none', textAlign: 'center',
                      borderLeft: `1px solid ${ACCENT}44`,
                    }}
                  >
                    {g.group}
                  </th>
                ))}
              </tr>
              <tr style={{ color: bithumbMuted }}>
                <th style={{ ...td, textAlign: 'left' }}> </th>
                {groups.flatMap((g) => g.cols.map((c, i) => (
                  <th
                    key={`${g.group}-${c.label}`}
                    style={{
                      ...td, textAlign: 'left',
                      borderLeft: i === 0 ? `1px solid ${ACCENT}44` : undefined,
                    }}
                  >
                    {c.label}
                  </th>
                )))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ ...td, color: ACCENT, fontWeight: 600 }}>{u('스팟')}</td>
                {groups.flatMap((g) => g.cols.map((c, i) => (
                  <td
                    key={`${g.group}-${c.label}`}
                    style={{
                      ...td,
                      borderLeft: i === 0 ? `1px solid ${ACCENT}33` : undefined,
                    }}
                  >
                    {c.value}
                  </td>
                )))}
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 14, overflowX: 'auto', fontSize: '0.66rem' }}>
          <table style={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%' }}>
            <tbody>
              {groups.map((g) => (
                <React.Fragment key={g.group}>
                  <tr>
                    <td
                      colSpan={2}
                      style={{
                        color: ACCENT, fontWeight: 600, padding: '10px 0 4px',
                        borderTop: `1px solid ${ACCENT}33`,
                      }}
                    >
                      {g.group}
                    </td>
                  </tr>
                  {g.cols.map((c) => (
                    <tr key={c.label}>
                      <td style={{
                        color: bithumbMuted, whiteSpace: 'nowrap',
                        padding: '2px 16px 2px 0', verticalAlign: 'top',
                      }}
                      >
                        {c.label}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', padding: '2px 0', lineHeight: 1.45 }}>
                        {c.hint}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ScoreModal({
  snap,
  onClose,
  t,
}: {
  snap: BithumbPaperSnapshot;
  onClose: () => void;
  t: DashboardT;
}) {
  const [fold, setFold] = useState(false);
  const [detail, setDetail] = useState(false);
  const stack = snap.live_stack ?? { days: [], span: null };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (detail) { setDetail(false); return; }
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, detail]);

  return createPortal(
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('bithumb.spot.score.aria')}
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.72)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          padding: 16, overflowY: 'auto',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#12100c',
            border: `1px solid ${ACCENT}55`,
            borderRadius: 8, padding: '14px 16px', maxWidth: 920, width: '100%',
            margin: '24px 0', minWidth: 0,
            overflowX: 'hidden',
            color: '#e8dcc0', fontSize: '0.78rem', lineHeight: 1.5,
          }}
        >
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'baseline', marginBottom: 10,
          }}
          >
            <strong style={{ color: ACCENT, fontSize: '0.95rem' }}>
              {t('bithumb.spot.score')}
            </strong>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'none', border: `1px solid ${ACCENT}55`, color: '#e8dcc0',
                borderRadius: 4, cursor: 'pointer', padding: '2px 8px',
              }}
            >
              {t('common.close')}
            </button>
          </div>

          <div style={{
            marginTop: 14, padding: '10px 12px', border: `1px solid ${ACCENT}44`,
            borderRadius: 6, background: '#0f0f0f',
          }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
              color: ACCENT, fontSize: '0.72rem', fontWeight: 600,
            }}
            >
              <button
                type="button"
                onClick={() => setFold((v) => !v)}
                style={{
                  background: 'none', border: 'none', color: ACCENT,
                  cursor: 'pointer', padding: 0, fontWeight: 600, fontSize: '0.72rem',
                }}
              >
                {fold ? '▸' : '▾'} {t('bithumb.spot.liveStack')}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setDetail(true); }}
                style={{
                  background: 'none', border: `1px solid ${ACCENT}55`, color: '#e8dcc0',
                  borderRadius: 4, cursor: 'pointer', padding: '1px 8px',
                  fontSize: '0.66rem',
                }}
              >
                {t('bithumb.spot.detail')}
              </button>
            </div>
            {fold ? null : <LiveStackTable stack={stack} t={t} />}
          </div>
        </div>
      </div>
      {detail ? (
        <StackRulesDetail snap={snap} onClose={() => setDetail(false)} t={t} />
      ) : null}
    </>,
    document.body,
  );
}

const BithumbSpotPaperPanel: React.FC = () => {
  const { locale, t } = useDashboardLocale();
  const [snap, setSnap] = useState<BithumbPaperSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [scoreOpen, setScoreOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const s = await fetchBithumbMidnightPaper();
      setSnap(s);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const rows = useMemo(() => (snap ? buildRows(snap, t) : []), [snap, t]);
  const sum = snap?.summary;
  const stackSpan = snap?.live_stack?.span;
  // 헤더 합/WR = 라이브스택 책 (Binance와 동일). paper 체결 n은 open/closed 칸.
  const wr = stackSpan?.win_rate_pct ?? stackSpan?.wr ?? sum?.wr;
  const sumNet = stackSpan?.sum_pct ?? stackSpan?.sum_net ?? sum?.sum_net;
  const bookN = stackSpan?.n ?? sum?.n;
  const target = sum?.wr_target ?? snap?.thesis_wr ?? 96;
  const wrColor = wr == null ? bithumbMuted : wr >= target ? POS : NEG;
  const cl = snap?.cluster;
  const alertType = t('alertType.segment_rise');
  // KR alert types are 2-char compounds (구간 상승 → 구간 / 상승). EN/FR stay one line.
  const alertTypeLines: [string, string | null, null] = locale === 'kr'
    ? [alertType.slice(0, 2), alertType.slice(2).trim() || null, null]
    : [alertType, null, null];
  const allowAlarm = allowlistSeedAlarm(snap?.allowlist_seed, t);

  return (
    <div
      className="binance-alert-panel bithumb-spot-panel"
      style={{
        padding: '14px 16px',
        borderRadius: 8,
        border: '1px solid #ff6c0066',
        background: 'linear-gradient(135deg, rgba(255,108,0,0.08) 0%, rgba(0,0,0,0.35) 100%)',
        minWidth: 0,
      }}
    >
      {scoreOpen && snap ? (
        <ScoreModal snap={snap} onClose={() => setScoreOpen(false)} t={t} />
      ) : null}

      <div className="binance-alert-header">
        <div className="binance-alert-header-top">
          <h3 style={{ ...bithumbH3, margin: 0 }}>{t('bithumb.spot.title')}</h3>
          <div className="binance-alert-header-actions">
            <button
              type="button"
              className={`bithumb-alert-refresh${allowAlarm ? ' binance-liq-retune-alert' : ''}`}
              onClick={() => setScoreOpen(true)}
              aria-label={t('bithumb.spot.score.aria')}
              title={allowAlarm ?? undefined}
              disabled={!snap}
            >
              <span className="binance-alert-action-full">{t('bithumb.spot.score')}</span>
              <span className="binance-alert-action-short">{t('bithumb.spot.score')}</span>
            </button>
          </div>
        </div>
        <div className="binance-alert-subtitle" style={{ color: bithumbMuted, fontSize: '0.72rem', lineHeight: 1.45 }}>
          {t('bithumb.spot.subtitle', { target })}
          {cl?.t0_kst
            ? ` · ${cl.t0_kst.slice(5, 16)}–${(cl.t1_kst || '').slice(11, 16)} · ${(cl.symbols || cl.allowlisted || []).join(' · ') || '–'}`
            : t('bithumb.spot.subtitle.clusterWait')}
          {snap?.updated_at
            ? t('bithumb.spot.subtitle.updated', { t: shortTime(snap.updated_at) })
            : ''}
        </div>
      </div>

      {loading && !snap && (
        <div style={{ color: bithumbMuted, fontSize: '0.78rem' }}>{t('common.loading')}</div>
      )}
      {error && (
        <div style={{ color: NEG, fontSize: '0.78rem', marginBottom: 8 }}>{error}</div>
      )}
      {snap?.last_error && (
        <div style={{ color: NEG, fontSize: '0.75rem', marginBottom: 8 }}>
          paper: {snap.last_error}
        </div>
      )}

      {snap && (
        <>
          <div className="binance-alert-stats">
            <Stat label="cluster" value={cl?.n_symbols ?? 0} color={ACCENT} />
            <Stat
              label="allow"
              value={sum?.n_allow ?? cl?.allowlisted?.length ?? 0}
              color={ACCENT}
            />
            <Stat
              label="ready"
              value={sum?.n_ready ?? 0}
              color={(sum?.n_ready ?? 0) > 0 ? POS : bithumbMuted}
            />
            <Stat label="open" value={snap.open?.length ?? 0} color={(snap.open?.length ?? 0) ? ACCENT : bithumbMuted} />
            <Stat
              label="wr"
              value={wr == null ? '–' : `${wr.toFixed(1)}%`}
              color={wrColor}
              sub={t('bithumb.spot.stat.wrTarget', { target })}
            />
            <Stat
              label={t('bithumb.spot.stat.sum')}
              value={pct(sumNet, 1)}
              color={(sumNet ?? 0) >= 0 ? POS : NEG}
              sub={t('bithumb.spot.stat.stackN', { n: bookN ?? 0 })}
            />
            <Stat label="mode" value="paper" />
            <Stat label="days" value={sum?.days_live != null ? sum.days_live.toFixed(2) : '–'} />
            <Stat label="ticks" value={snap.ticks_seen != null ? String(snap.ticks_seen) : '–'} />
          </div>

          <div className="binance-alert-section-head">
            <div className="binance-alert-section-title">
              {t('binance.tradeHistory.title')}
            </div>
          </div>

          <div className="binance-alert-table-wrap">
            <table
              key={locale}
              className="binance-alert-table"
              style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}
            >
              <colgroup>
                <col className="binance-alert-col-symbol" />
                <col className="binance-alert-col-exchange" />
                <col className="binance-alert-col-alert-type" />
                <col className="binance-alert-col-status" />
                <col className="binance-alert-col-detail" />
                <col className="binance-alert-col-sizing" />
                <col className="binance-alert-col-pnl" />
                <col className="binance-alert-col-chart" />
              </colgroup>
              <thead>
                <tr>
                  <th className="binance-alert-col-symbol" style={th}>
                    {t('binance.table.header.symbol')}
                  </th>
                  <th
                    className="binance-alert-col-exchange"
                    style={th}
                    title={t('binance.table.header.exchange.title')}
                  >
                    Ex
                  </th>
                  <th className="binance-alert-col-alert-type" style={th}>
                    {t('binance.table.header.alert')}
                  </th>
                  <th className="binance-alert-col-status" style={th}>
                    {t('binance.table.header.status')}
                  </th>
                  <th className="binance-alert-col-detail" style={th}>
                    {t('binance.table.header.detail')}
                  </th>
                  <th className="binance-alert-col-sizing" style={th}>
                    {t('binance.table.header.sizing')}
                  </th>
                  <th className="binance-alert-col-pnl" style={th}>
                    {t('binance.table.header.pnl')}
                  </th>
                  <th className="binance-alert-col-chart" style={th}>
                    {t('binance.table.header.chart')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ ...td, color: bithumbMuted, textAlign: 'center' }}>
                      {t('bithumb.spot.table.empty')}
                    </td>
                  </tr>
                )}
                {rows.map((row) => (
                  <tr key={row.key} className={row.open ? 'broker-row-open' : undefined}>
                    <td
                      className="binance-alert-col-symbol"
                      style={td}
                      title={`${row.base} · ${row.market}${row.tier ? ` · ${row.tier}` : ''}`}
                    >
                      <ThreeLineStack
                        mutedColor={bithumbMuted}
                        lines={[row.base, row.market, row.tier ? `tier ${row.tier}` : `${row.base}USDT`]}
                      />
                    </td>
                    <td className="binance-alert-col-exchange" style={td}>
                      <ThreeLineStack
                        mutedColor={bithumbMuted}
                        lines={[
                          <span
                            style={{ color: ACCENT, fontWeight: 600 }}
                            title={t('binance.exchange.full.bithumb')}
                          >
                            {t('binance.exchange.short.bithumb')}
                          </span>,
                          null,
                          null,
                        ]}
                      />
                    </td>
                    <td className="binance-alert-col-alert-type" style={td}>
                      <div title={alertType}>
                        <ThreeLineStack lines={alertTypeLines} />
                      </div>
                    </td>
                    <td className="binance-alert-col-status" style={td}>
                      <div className="binance-status-stack">
                        <StatusLine
                          time={row.alertHm}
                          label={t('binance.status.alert')}
                          color={ACCENT}
                        />
                        <StatusLine
                          time={row.alertHm}
                          label={row.flowLabel}
                          color={row.flowColor}
                        />
                        <StatusLine
                          label={row.open ? t('binance.status.open') : '–'}
                          color={row.open ? ACCENT : bithumbMuted}
                        />
                      </div>
                    </td>
                    <td
                      className="binance-alert-col-detail"
                      style={td}
                      title={row.detail.filter(Boolean).join(' · ')}
                    >
                      <div className="binance-alert-detail-text">
                        <ThreeLineStack mutedColor={bithumbMuted} lines={row.detail} />
                      </div>
                    </td>
                    <td className="binance-alert-col-sizing" style={{ ...td, color: bithumbMuted }}>
                      {row.q1h != null ? `q ${row.q1h.toFixed(0)}` : '–'}
                    </td>
                    <td
                      className="binance-alert-col-pnl"
                      style={{
                        ...td,
                        color: row.net == null ? bithumbMuted : row.net >= 0 ? POS : NEG,
                      }}
                    >
                      {row.net == null ? '–' : (
                        <div className="binance-pnl-stack">
                          <span>{pct(row.net, 2)}</span>
                        </div>
                      )}
                    </td>
                    <td className="binance-alert-col-chart" style={{ ...td, color: bithumbMuted }}>
                      –
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default BithumbSpotPaperPanel;
